import React from 'react';
import { View, Animated, StyleSheet, Platform } from 'react-native';

// ────── Web : mock complet avec les vrais exports ──────

// SceneMap : transforme { Home: HomeScreen } → (props) => <HomeScreen />
export const SceneMap = (scenes) => {
  return ({ route }) => {
    const Component = scenes[route.key];
    return Component ? <Component {...route} /> : null;
  };
};

// TabBar stylisable via props
export const TabBar = ({
  navigationState,
  jumpTo,
  position,
  style,
  indicatorStyle,
  activeColor,
  inactiveColor,
  renderLabel,
  tabStyle,
  pressOpacity = 0.5,
  onTabPress,
  ...rest
}) => {
  return (
    <View style={[styles.tabBar, style]}>
      {navigationState.routes.map((route, i) => {
        const isActive = navigationState.index === i;
        const color = isActive
          ? (activeColor || '#007AFF')
          : (inactiveColor || '#8e8e93');

        return (
          <View
            key={route.key || i}
            style={[styles.tabItem, tabStyle, isActive && [styles.tabItemActive, indicatorStyle]]}
            onClick={() => {
              if (onTabPress) onTabPress({ route, preventDefault: () => {} });
              jumpTo(route.key);
            }}
          >
            {renderLabel
              ? renderLabel({ route, focused: isActive, color })
              : <span style={{ color }}>{route.title || route.key}</span>
            }
          </View>
        );
      })}
    </View>
  );
};

// TabBarIndicator (ligne qui suit l’onglet actif)
export const TabBarIndicator = () => {
  return <View style={styles.indicator} />;
};

// TabBarItem (optionnel, utilisé par certains)
export const TabBarItem = ({ children, ...props }) => {
  return <View {...props}>{children}</View>;
};

// TabView : le cœur du composant
export const TabView = ({
  navigationState,
  renderScene,
  renderTabBar = (props) => <TabBar {...props} />,
  onIndexChange,
  initialLayout = { width: 375, height: 0 },
  ...rest
}) => {
  const [index, setIndex] = React.useState(navigationState.index || 0);

  React.useEffect(() => {
    setIndex(navigationState.index);
  }, [navigationState.index]);

  const position = React.useMemo(() => new Animated.Value(index), [index]);

  React.useEffect(() => {
    Animated.timing(position, {
      toValue: index,
      duration: 150,
      useNativeDriver: false,
    }).start();
  }, [index, position]);

  const handleIndexChange = (newIndex) => {
    setIndex(newIndex);
    onIndexChange?.(newIndex);
  };

  const propsForTabBar = {
    navigationState: { ...navigationState, index },
    position,
    jumpTo: (key) => {
      const routeIndex = navigationState.routes.findIndex(r => r.key === key);
      if (routeIndex !== -1) handleIndexChange(routeIndex);
    },
  };

  return (
    <View style={{ flex: 1 }}>
      {renderTabBar(propsForTabBar)}
      {navigationState.routes.map((route, i) => (
        <View key={route.key || i} style={{ flex: 1, display: i === index ? 'flex' : 'none' }}>
          {renderScene({ route, jumpTo: propsForTabBar.jumpTo })}
        </View>
      ))}
    </View>
  );
};

// Types (on les réexporte vides pour éviter les erreurs TypeScript)
export type {
  NavigationState,
  Route,
  SceneRendererProps,
  TabDescriptor,
} from './types';

// Export par défaut (utilisé quand on fait import TabView from 'react-native-tab-view')
export default TabView;

// Styles simples mais fonctionnels
const styles = StyleSheet.create({
  tabBar: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  tabItem: {
    flex: 1,
    padding: 12,
    alignItems: 'center',
  },
  tabItemActive: {
    borderBottomWidth: 2,
    borderBottomColor: '#007AFF',
  },
  indicator: {
    height: 2,
    backgroundColor: '#007AFF',
  },
});